import * as React from "react"
import { useInRouterContext, useLocation, useNavigate } from "react-router-dom"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command"
import { NavIcon, type NavIconName } from "@/components/layout/navIcons"

/**
 * ⌘K page search. Humblytics has no in-app chat: questions go to the user's own
 * agent over MCP or the API, so the palette only jumps between pages.
 */

type CommandSearchContextValue = { open: boolean; setOpen: (open: boolean) => void }

const CommandSearchContext = React.createContext<CommandSearchContextValue>({ open: false, setOpen: () => {} })

export const useCommandSearch = () => React.useContext(CommandSearchContext)

type Destination = { label: string; path: string; icon: NavIconName; keywords?: string[] }

const SCOPED: { group: string; items: Destination[] }[] = [
  {
    group: "Observe",
    items: [
      { label: "Analytics", path: "/traffic", icon: "analytics", keywords: ["overview", "traffic", "visitors"] },
      { label: "Pages", path: "/pages", icon: "analytics", keywords: ["top pages"] },
      { label: "Traffic sources", path: "/channels", icon: "analytics", keywords: ["channels", "referrers", "utm"] },
      { label: "Devices and locations", path: "/devices", icon: "analytics", keywords: ["countries", "browsers"] },
      { label: "Clicks and forms", path: "/clicks", icon: "clicks", keywords: ["events", "submissions"] },
      { label: "Heatmaps", path: "/heatmaps", icon: "heatmaps", keywords: ["scroll", "click map"] },
      { label: "Funnels", path: "/funnel-analyzer", icon: "funnels", keywords: ["conversion", "drop off"] },
      { label: "Attribution", path: "/attribution", icon: "attribution", keywords: ["revenue", "ads", "campaigns"] },
    ],
  },
  {
    group: "Optimize",
    items: [
      { label: "Experiments", path: "/split-testing", icon: "experiments", keywords: ["a/b tests", "split tests"] },
      { label: "Test planner", path: "/test-planner", icon: "experiments", keywords: ["sample size", "duration"] },
    ],
  },
  {
    group: "Agent and data",
    items: [
      { label: "API & MCP", path: "/api", icon: "api", keywords: ["keys", "claude", "codex", "agent"] },
      { label: "Connectors", path: "/connectors", icon: "connectors", keywords: ["stripe", "meta", "google ads"] },
      { label: "Export", path: "/export", icon: "export", keywords: ["csv", "download"] },
      { label: "Marketing tools", path: "/tools", icon: "tools", keywords: ["utm generator", "calculator"] },
      { label: "Resources", path: "/resources", icon: "resources", keywords: ["docs", "help"] },
      { label: "Settings", path: "/settings", icon: "settings", keywords: ["team", "install", "tracking"] },
    ],
  },
]

function CommandSearchDialog({ open, setOpen }: CommandSearchContextValue) {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const base = pathname.match(/^\/(?:workspace|p)\/[^/]+/)?.[0] ?? null
  const site = new URLSearchParams(search).get("site")

  const go = (to: string) => {
    setOpen(false)
    navigate(to)
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Jump to a page" />
      <CommandList className="max-h-[360px]">
        <CommandEmpty>No pages match.</CommandEmpty>
        <CommandGroup heading="Go to">
          <CommandItem value="Home" onSelect={() => go("/dashboard")}>
            <NavIcon name="home" size={16} />
            Home
          </CommandItem>
          <CommandItem value="Account settings profile billing plan" onSelect={() => go("/user-settings")}>
            <NavIcon name="settings" size={16} />
            Account settings
          </CommandItem>
        </CommandGroup>
        {base &&
          SCOPED.map((section) => (
            <CommandGroup key={section.group} heading={section.group}>
              {section.items.map((item) => (
                <CommandItem
                  key={item.path}
                  value={[item.label, ...(item.keywords ?? [])].join(" ")}
                  onSelect={() => go(`${base}${item.path}${site ? `?site=${site}` : ""}`)}
                >
                  <NavIcon name={item.icon} size={16} />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
      </CommandList>
      <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs text-foreground/50">
        <span>Ask questions from your own agent through API & MCP.</span>
        <CommandShortcut>Esc to close</CommandShortcut>
      </div>
    </CommandDialog>
  )
}

export function CommandSearchProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false)
  const inRouter = useInRouterContext()

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        setOpen((v) => !v)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  return (
    <CommandSearchContext.Provider value={{ open, setOpen }}>
      {children}
      {inRouter && <CommandSearchDialog open={open} setOpen={setOpen} />}
    </CommandSearchContext.Provider>
  )
}
